import type MarkdownIt from "markdown-it"
import { z } from "zod"
import { formatPathReferenceLabel, isAbsolutePathReference } from "./pathReference"

type OutputDirective = {
    name: string
    attributes: Record<string, string>
    source: string
}

// Bound lookahead on malformed/streaming output, including repeated openers.
const MAX_DIRECTIVE_LENGTH = 32_768
const MAX_DIRECTIVE_LINES = 64
const MAX_ATTRIBUTES = 32
const NAME = /^[a-z][a-z0-9_-]{0,63}/i
const QUOTES = new Map([
    ['"', '"'],
    ["'", "'"],
    ["“", "”"],
    ["‘", "’"],
])
const ESCAPES = new Map([
    ["n", "\n"],
    ["r", "\r"],
    ["t", "\t"],
    ["\\", "\\"],
])

function parseDirective(source: string): OutputDirective | null {
    const prefix = /^::([a-z][a-z0-9_-]{0,63})\{/i.exec(source)
    if (!prefix) return null
    const attributes = new Map<string, string>()
    let index = prefix[0].length

    while (index < source.length) {
        while (/\s/.test(source[index] ?? "") && index < source.length) index++
        if (source[index] === "}") {
            return { name: prefix[1], attributes: Object.fromEntries(attributes), source: source.slice(0, index + 1) }
        }
        const key = NAME.exec(source.slice(index))?.[0]
        if (!key || attributes.has(key) || attributes.size >= MAX_ATTRIBUTES) return null
        index += key.length
        while (/\s/.test(source[index] ?? "") && index < source.length) index++
        if (source[index++] !== "=") return null
        while (/\s/.test(source[index] ?? "") && index < source.length) index++

        let value = ""
        const quote = QUOTES.get(source[index])
        if (quote) {
            index++
            while (index < source.length && source[index] !== quote) {
                const character = source[index++]
                if (character === "\\" && index < source.length) {
                    const escaped = source[index++]
                    value += escaped === quote || QUOTES.has(escaped) ? escaped : (ESCAPES.get(escaped) ?? `\\${escaped}`)
                } else {
                    value += character
                }
            }
            if (source[index++] !== quote) return null
        } else {
            const start = index
            while (index < source.length && !/[\s{}"'“”‘’]/.test(source[index])) index++
            if (index === start) return null
            value = source.slice(start, index)
        }
        attributes.set(key, value)
        if (index < source.length && !/[\s}]/.test(source[index])) return null
    }
    return null
}

const lineNumber = z
    .string()
    .regex(/^[1-9]\d*$/)
    .transform(Number)
    .pipe(z.number().int().positive().max(Number.MAX_SAFE_INTEGER))
const codeCommentSchema = z
    .object({
        title: z.string().trim().min(1).max(512),
        body: z.string().min(1).max(16_384),
        file: z
            .string()
            .min(1)
            .max(4096)
            .refine((path) => path.trim().length > 0 && !/\p{Cc}/u.test(path) && (isAbsolutePathReference(path) || !/^[a-z][a-z\d+.-]*:/i.test(path))),
        start: lineNumber.optional(),
        end: lineNumber.optional(),
        priority: z
            .string()
            .regex(/^[0-3]$/)
            .transform(Number)
            .optional(),
        confidence: z
            .string()
            .regex(/^(?:0(?:\.\d+)?|1(?:\.0+)?)$/)
            .transform(Number)
            .optional(),
    })
    .refine(({ start, end }) => end === undefined || (start !== undefined && end >= start))

type DirectiveRenderer = (directive: OutputDirective, markdown: MarkdownIt) => string | null

const renderCodeComment: DirectiveRenderer = (directive, markdown) => {
    const parsed = codeCommentSchema.safeParse(directive.attributes)
    if (!parsed.success) return null
    const { title, body, file, start, end, priority, confidence } = parsed.data
    const escape = markdown.utils.escapeHtml
    const titlePriority = /^\[P([0-3])\]\s*/.exec(title)
    const displayedPriority = priority ?? (titlePriority ? Number(titlePriority[1]) : undefined)
    const heading = titlePriority && Number(titlePriority[1]) === displayedPriority ? title.slice(titlePriority[0].length) : title
    const range = start ? `:${start}${end && end !== start ? `–${end}` : ""}` : ""
    const label = `${formatPathReferenceLabel(file)}${range}`
    const badge = displayedPriority === undefined ? "" : `<span class="shrink-0 border border-border px-1.5 font-mono text-xs">P${displayedPriority}</span>`
    const confidenceLabel = confidence === undefined ? "" : `<span class="text-xs text-muted">${Math.round(confidence * 100)}% confidence</span>`

    // These attributes use MarkdownMessage's existing exact-file click routing.
    // The caller sanitizes the complete HTML, including Markdown in the body.
    return `<article aria-label="Code review finding" class="my-2 min-w-0 border border-border bg-base-200/40 p-3 text-base-content">
<div class="flex items-baseline gap-2">${badge}<h4 class="min-w-0 font-semibold">${escape(heading)}</h4></div>
<div class="my-2 whitespace-pre-wrap">${markdown.renderInline(body)}</div>
<div class="flex flex-wrap items-baseline gap-x-3 gap-y-1"><a href="#" data-wildlark-file-link="true" data-wildlark-file-path="${escape(file)}"${start ? ` data-wildlark-file-line="${start}"` : ""} title="${escape(file + range)}" class="font-mono text-xs">${escape(label)}</a>${confidenceLabel}</div>
</article>\n`
}

// Register typed presentation here. Provider directives never execute actions.
const DIRECTIVE_RENDERERS = new Map<string, DirectiveRenderer>([["code-comment", renderCodeComment]])

function renderDirective(directive: OutputDirective, markdown: MarkdownIt): string {
    const rendered = DIRECTIVE_RENDERERS.get(directive.name)?.(directive, markdown)
    if (rendered) return rendered
    const escape = markdown.utils.escapeHtml
    const title = directive.attributes.title
    const label = `${directive.name}${title ? `: ${title}` : ""}`
    return `<details class="my-2 min-w-0 border border-border p-3"><summary class="cursor-pointer text-muted">${escape(label)}</summary><pre class="mt-2 whitespace-pre-wrap break-words text-xs">${escape(directive.source)}</pre></details>\n`
}

export function markdownOutputDirectives(markdown: MarkdownIt): void {
    markdown.block.ruler.before(
        "paragraph",
        "wildlark_output_directive",
        (state, startLine, endLine, silent) => {
            if (state.sCount[startLine] - state.blkIndent >= 4) return false
            const start = state.bMarks[startLine] + state.tShift[startLine]
            if (!state.src.startsWith("::", start)) return false
            const limitLine = Math.min(endLine, startLine + MAX_DIRECTIVE_LINES)
            const source = state.src.slice(start, Math.min(state.eMarks[limitLine - 1], start + MAX_DIRECTIVE_LENGTH))
            const directive = parseDirective(source)
            if (!directive) return false
            const end = start + directive.source.length
            let nextLine = startLine + 1
            while (nextLine < limitLine && state.bMarks[nextLine] < end) nextLine++
            if (state.src.slice(end, state.eMarks[nextLine - 1]).trim()) return false
            if (silent) return true

            const token = state.push("wildlark_output_directive", "", 0)
            token.block = true
            token.map = [startLine, nextLine]
            token.content = renderDirective(directive, markdown)
            state.line = nextLine
            return true
        },
        { alt: ["paragraph"] }
    )
    markdown.renderer.rules.wildlark_output_directive = (tokens, index) => tokens[index].content
}
